import { supabase } from '../lib/supabase';

/**
 * User Profile Service
 * Handles all CRUD operations for user profiles with proper snake_case ↔ camelCase conversion
 */
export const userProfileService = {
  /**
   * Get current user's profile
   * @returns {Promise<Object>} User profile data in camelCase
   */
  async getCurrentProfile() {
    try {
      // Get authenticated user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Fetch profile from database
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (error) throw error;

      // Convert snake_case to camelCase for React
      return {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        currency: data?.currency,
        phoneNumber: data?.phone_number,
        country: data?.country,
        businessName: data?.business_name,
        domain: data?.domain,
        employeeCount: data?.employee_count,
        monthlyRevenue: data?.monthly_revenue,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Update current user's profile
   * @param {Object} profileData - Profile data in camelCase
   * @returns {Promise<Object>} Updated profile data in camelCase
   */
  async updateProfile(profileData) {
    try {
      // Get authenticated user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const updateData = {};
      if (profileData?.fullName !== undefined) updateData.full_name = profileData?.fullName;
      if (profileData?.phoneNumber !== undefined) updateData.phone_number = profileData?.phoneNumber;
      if (profileData?.country !== undefined) updateData.country = profileData?.country;
      if (profileData?.businessName !== undefined) updateData.business_name = profileData?.businessName;
      if (profileData?.domain !== undefined) updateData.domain = profileData?.domain;
      if (profileData?.employeeCount !== undefined) updateData.employee_count = profileData?.employeeCount;
      if (profileData?.currency !== undefined) updateData.currency = profileData?.currency;
      if (profileData?.monthlyRevenue !== undefined) updateData.monthly_revenue = profileData?.monthlyRevenue;

      // Update profile in database
      const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', user?.id)?.select()?.single();

      if (error) throw error;

      // Convert snake_case back to camelCase for React
      return {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        currency: data?.currency,
        phoneNumber: data?.phone_number,
        country: data?.country,
        businessName: data?.business_name,
        domain: data?.domain,
        employeeCount: data?.employee_count,
        monthlyRevenue: data?.monthly_revenue,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
};